/**
 * Page — wrapper structurant des routes authentifiees.
 *
 * Standardise le padding, la max-width et le layout vertical.
 *   <Page>
 *     <Page.Header eyebrow="Atelier" title="Œuvres" actions={<Button>Nouvelle</Button>} />
 *     <PageToolbar search={...} />
 *     ...
 *   </Page>
 *
 * Tailles : 'default' (max 1280px) | 'narrow' (max 960px) | 'wide' (max 1600px) | 'full' (no cap).
 *
 * Adapte de scapino-portal/frontend/src/components/ui/Page.tsx.
 */

import type { HTMLAttributes, ReactNode } from 'react'

import './Page.css'

type PageWidth = 'default' | 'narrow' | 'wide' | 'full'

interface PageProps extends HTMLAttributes<HTMLDivElement> {
  width?: PageWidth
  children: ReactNode
}

const WIDTH_CLASS: Record<PageWidth, string> = {
  default: '',
  narrow: ' page-narrow',
  wide: ' page-wide',
  full: ' page-full',
}

function Page({ width = 'default', children, className, ...rest }: PageProps) {
  return (
    <div className={`page${WIDTH_CLASS[width]}${className ? ` ${className}` : ''}`} {...rest}>
      {children}
    </div>
  )
}

interface PageHeaderProps {
  title: ReactNode
  subtitle?: ReactNode
  actions?: ReactNode
  eyebrow?: ReactNode
  meta?: ReactNode
  className?: string
}

function PageHeader({ title, subtitle, actions, eyebrow, meta, className }: PageHeaderProps) {
  return (
    <header className={`page-header ui-rule--masthead${className ? ` ${className}` : ''}`}>
      <div className="page-header-title-group">
        {eyebrow && <p className="ui-eyebrow">{eyebrow}</p>}
        <h1 className="page-header-title">{title}</h1>
        {subtitle && <p className="page-header-subtitle">{subtitle}</p>}
        {meta && <div className="page-header-meta">{meta}</div>}
      </div>
      {actions && <div className="page-header-actions">{actions}</div>}
    </header>
  )
}

Page.Header = PageHeader

export default Page
